<template>
  <div>
    <b-overlay class="custom-overlay" :show="displayOverlay" rounded="sm">
      <b-card no-body>
          <b-card-header>
            Sincronizar noticia
          </b-card-header>
          <b-card-body>
            <div class="row">
              <div class="col-md-2">
                <label for="" class='ml-2'>De</label>
                <div class="d-flex align-items-center">
                  <feather-icon class='mr-1' icon="CalendarIcon" size="16" />
                  <flat-pickr
                    v-model="dtFrom"
                    class="form-control-sm form-date"
                    placeholder="De"
                    :config="{
                      enableTime: false,
                      dateFormat: 'Y-m-d',
                      altInput: true,
                      altFormat: 'd/m/Y',
                      allowInput: true,
                    }"
                  />
                </div>
              </div>

              <div class="col-md-2">
                <label for="" class='ml-2'>Até</label>
                <div class="d-flex align-items-center">
                  <feather-icon class='mr-1' icon="CalendarIcon" size="16" />
                  <flat-pickr
                    v-model="dtTo"
                    class="form-control-sm form-date"
                    placeholder="Até"
                    :config="{
                      enableTime: false,
                      dateFormat: 'Y-m-d',
                      altInput: true,
                      altFormat: 'd/m/Y',
                      allowInput: true,
                    }"
                  />
                </div>
              </div>
              <div class="col-md-12"> 
                <hr class="mb-3 mt-2">
              </div>
              
              <div class="col-md-4">
                <div class="form-label-group">
                  <b-form-input v-model="originCode" placeholder="Código de origem"/>
                  <label>Código de origem</label>
                </div>
                
              </div>
              <div class="col-md-4">
                <div class="form-label-group">
                  <b-form-input v-model="channelCode" placeholder="Código de canal" />
                  <label>Código de canal</label>
                </div>
              </div>
              <div class="col-md-12">
                <b-form-group>
                  <b-form-checkbox
                    id="indexPdf"
                    name="indexPdf"
                    v-model="indexPdf"
                  >
                    Indexar dados do pdf
                  </b-form-checkbox>
                </b-form-group>
              </div>
            </div>

            <b-button variant="primary" class="mt-2" :disabled="isRunning" @click="synchronize">
              Sincronizar
            </b-button>
            <b-button variant="outline-primary" class="mt-2 ml-1" :disabled="isRunning" @click="reconcile">
              Reconciliar
            </b-button>
            <small class="d-block text-muted mt-1">
              Sincronizar reenvia todas as notícias do período. Reconciliar compara o período com o Elasticsearch e
              corrige só o que estiver diferente. Notícias removidas do Elasticsearch não voltam em nenhum dos dois.
            </small>

            <div v-if="job" class="mt-2">
              <h6 class="mb-50">{{ job.type === 'reconcile' ? 'Reconciliação' : 'Sincronização' }}: {{ statusLabel }}</h6>
              <div>
                Analisadas: {{ job.scanned }} | Indexadas: {{ job.indexed }} | Ignoradas: {{ job.skipped }} |
                Com erro: {{ job.failed }} | Retiradas da busca: {{ job.removed }} |
                Marcadas como removidas: {{ job.excluded }}
              </div>
              <div v-if="job.message" class="text-muted">{{ job.message }}</div>
              <div v-if="job.errorNews && job.errorNews.length" class="mt-1">
                <span>Notícias com erro (serão reprocessadas automaticamente):</span>
                <ul class="mb-0">
                  <li v-for="item in job.errorNews.slice(0, 20)" :key="item.newsId">{{ item.newsId }}: {{ item.reason }}</li>
                </ul>
              </div>
            </div>
          </b-card-body>
      </b-card>
    </b-overlay>
    
  </div>
</template>

<script>
import { BButton, BFormInput, BCard, BCardBody, BCardHeader, BOverlay, BFormCheckbox, BFormGroup } from "bootstrap-vue";
import newsService from '@/providers/newsService';
import messagesNotifyHelper from '@/helpers/messages-notify-helper';
import { ref, computed, onBeforeUnmount } from "@vue/composition-api";
import flatPickr from "vue-flatpickr-component";
import convertDatetimeHelper from '@/helpers/convert-datetime-helper';

export default {
  components: {
    BButton,
    BFormInput,
    BCard, 
    BCardBody, 
    BCardHeader,
    BOverlay,
    flatPickr,
    BFormCheckbox, 
    BFormGroup
  },
  setup() {
    const dtFrom = ref(convertDatetimeHelper.convertDateToStringFormatFrom(convertDatetimeHelper.getFirstDayOfCurrentYear()));
    const dtTo = ref(convertDatetimeHelper.convertDateToStringFormatFrom(convertDatetimeHelper.getLastDayOfCurrentYear()));
    const originCode = ref('250,131,132,54,122');
    const channelCode = ref('');
    const indexPdf = ref(false);
    const displayOverlay = ref(false);
    const job = ref(null);
    let pollingTimer = null;

    const isRunning = computed(() => !!job.value && job.value.status === 'processing');
    const statusLabel = computed(() => {
      const labels = {
        processing: 'em andamento',
        finished: 'concluída',
        finished_with_errors: 'concluída com erros',
        error: 'interrompida por erro',
      };
      return labels[job.value?.status] || job.value?.status || '';
    });

    const stopPolling = () => {
      if (pollingTimer) {
        clearTimeout(pollingTimer);
        pollingTimer = null;
      }
    };

    // O processamento roda em segundo plano no backend: consulta o progresso até terminar
    const pollJob = (jobId) => {
      newsService.getElasticSyncJob(jobId)
        .then((data) => {
          job.value = data;
          if (data.status === 'processing') {
            pollingTimer = setTimeout(() => pollJob(jobId), 4000);
            return;
          }
          if (data.status === 'error') {
            messagesNotifyHelper.errorNotify("Sincronização de noticia", data.message || "Erro ao sincronizar as notícias");
          } else {
            messagesNotifyHelper.successNotify("Sincronização de noticia", "Processo finalizado");
          }
        })
        .catch(err => {
          console.log(err);
          messagesNotifyHelper.errorNotify("Sincronização de noticia", "Erro ao consultar o andamento da sincronização");
        });
    };

    const startJob = (request) => {
      stopPolling();
      displayOverlay.value = true;
      const form = {
        from: dtFrom.value,
        to: dtTo.value,
        originCode: originCode.value,
        channelCode: channelCode.value,
        indexPdf: String(indexPdf.value),
      };
      request(form)
        .then((data) => {
          displayOverlay.value = false;
          job.value = { status: 'processing', scanned: 0, indexed: 0, skipped: 0, failed: 0, removed: 0, excluded: 0, errorNews: [] };
          messagesNotifyHelper.successNotify("Sincronização de noticia", "Processo iniciado, acompanhe o andamento abaixo");
          pollJob(data.jobId);
        })
        .catch(err => {
          displayOverlay.value = false;
          console.log(err);
          messagesNotifyHelper.errorNotify("Sincronização de noticia", err?.response?.data?.message || "Erro ao iniciar a sincronização");
        });
    };

    const synchronize = () => startJob((form) => newsService.startElasticSyncJob(form));
    const reconcile = () => startJob((form) => newsService.startElasticReconcileJob(form));

    onBeforeUnmount(stopPolling);

      return {
        dtFrom,
        dtTo,
        originCode,
        channelCode,
        synchronize,
        reconcile,
        indexPdf,
        displayOverlay,
        job,
        isRunning,
        statusLabel
      }
  }
};
</script>

<style>
</style>